import React from 'react';
import {View, Text, ScrollView} from 'react-native';
import {G, ForeignObject} from 'react-native-svg';
import moment from 'moment';
import _ from 'lodash';
import {Colors, Typography} from 'src/styles';

// props: x, y, datum, data, programLines, envLines, method, styles, stateText, maxHeight?
export const CombinedFlyout = (props: any) => {
  const {
    x,
    y,
    datum,
    data = [],
    programLines = [],
    envLines = [],
    method,
    styles,
    stateText,
    maxHeight = 260,
  } = props;

  const dayIndex = datum?.x; // 1-based
  const dayTs = data?.[dayIndex - 1]?.startTimestamp;
  const dayKey = dayTs ? moment(dayTs).format('YYYY-MM-DD') : '';

  const dayProgramPoints = _.flatMap(programLines, (l: any) =>
    (l.points || [])
      .filter((p: any) => p.x === dayIndex)
      .map((p: any) => ({...p, state: l.state})),
  );
  const p0 = dayProgramPoints[0];

  const modeState =
    _.head(
      _.orderBy(
        Object.entries(
          _.countBy(dayProgramPoints.map((p: any) => p.state || '')),
        ),
        ([, count]) => Number(count),
        'desc',
      ),
    )?.[0] ||
    p0?.state ||
    '';

  const dayEnvItems = _.flatMap(envLines, (l: any) => l.values?.[dayKey] || []);

  const typeMeta = (t?: string) => {
    if (t === 'change') {
      return {label: 'Change', color: Colors.TEAL_600};
    }
    if (t === 'factor') {
      return {label: 'Factor', color: Colors.ORANGE_300};
    }
    return {label: 'Item', color: Colors.PRIMARY_600};
  };

  // layout
  const CARD_W = 280; // a touch wider for scrollable content
  const xLeft = Math.max(x - CARD_W / 2, 10);
  const yTop = Math.max(y - (maxHeight - 40), 10); // bias upward; keeps more of the card in-bounds

  return (
    <G>
      <ForeignObject
        x={xLeft}
        y={yTop}
        width={CARD_W}
        height={maxHeight + 60} // give the FO enough space to hold the card
        // eslint-disable-next-line react-native/no-inline-styles
        style={{overflow: 'visible'}}>
        <View style={[styles.card, {width: CARD_W}]}>
          {/* Scrollable content */}
          <ScrollView
            style={{maxHeight}}
            // eslint-disable-next-line react-native/no-inline-styles
            contentContainerStyle={{paddingBottom: 4}}
            // RN Web: ScrollView handles overflow automatically; no need for overflowY
            // Native: wheel/touch scrolling works fine inside ForeignObject
          >
            {/* Date */}
            <Text style={styles.captionMediumTxt}>Date</Text>
            <Text style={styles.captionTxt}>
              {dayTs ? moment(dayTs).format('MM/DD/YY') : ''}
            </Text>

            {/* Collectors */}
            {p0?.collectors?.length ? (
              <>
                <Text style={[styles.captionMediumTxt, styles.mt_10]}>
                  Collector
                </Text>
                {p0.collectors.map((c: string, i: number) => (
                  <Text key={`collector-${i}`} style={styles.captionTxt}>
                    {c}
                  </Text>
                ))}
              </>
            ) : null}

            {/* Program metrics */}
            {p0 ? (
              method === 'task_analysis' || method === 'trial_by_trial' ? (
                <>
                  <Text style={[styles.captionMediumTxt, styles.mt_10]}>
                    Percent Correct
                  </Text>
                  <Text style={styles.captionTxt}>{Math.ceil(p0.y)}%</Text>
                </>
              ) : method === 'duration' && p0.duration !== '' ? (
                <>
                  <Text style={[styles.captionMediumTxt, styles.mt_10]}>
                    Total Duration
                  </Text>
                  <Text style={styles.captionTxt}>{p0.duration}</Text>
                </>
              ) : method === 'rate' ? (
                <>
                  <Text style={[styles.captionMediumTxt, styles.mt_10]}>
                    Rate
                  </Text>
                  <Text style={styles.captionTxt}>{p0.y?.toFixed(2)}</Text>
                </>
              ) : method === 'frequency' ? (
                <>
                  <Text style={[styles.captionMediumTxt, styles.mt_10]}>
                    Total Instances
                  </Text>
                  <Text style={styles.captionTxt}>{p0.y}</Text>
                </>
              ) : method === 'interval' ? (
                <>
                  <Text style={[styles.captionMediumTxt, styles.mt_10]}>
                    Behavior % Responding
                  </Text>
                  <Text style={styles.captionTxt}>{Math.ceil(p0.y)}%</Text>
                </>
              ) : null
            ) : null}

            {/* Target */}
            {p0?.target ? (
              <>
                <Text style={[styles.captionMediumTxt, styles.mt_10]}>
                  Target
                </Text>
                <Text style={styles.captionTxt}>{p0.target}</Text>
              </>
            ) : null}

            {/* Status */}
            {!!modeState && (
              <>
                <Text style={[styles.captionMediumTxt, styles.mt_10]}>
                  Status
                </Text>
                <Text style={styles.captionTxt}>{stateText(modeState)}</Text>
              </>
            )}

            {/* Environmental section */}
            {dayEnvItems?.length ? (
              <>
                <View
                  // eslint-disable-next-line react-native/no-inline-styles
                  style={{height: 1, backgroundColor: '#eee', marginTop: 10}}
                />
                <Text style={[styles.captionMediumTxt, styles.mt_10]}>
                  Environmental
                </Text>
                {dayEnvItems.map((env: any, i: number) => {
                  const meta = typeMeta(env?.type);
                  return (
                    <View key={`env-${i}`} style={styles.mt_10}>
                      <View
                        // eslint-disable-next-line react-native/no-inline-styles
                        style={{flexDirection: 'row', alignItems: 'center'}}>
                        <View
                          // eslint-disable-next-line react-native/no-inline-styles
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: 8,
                            marginRight: 6,
                            backgroundColor: meta.color,
                          }}
                        />
                        <Text
                          numberOfLines={1}
                          style={[
                            Typography.CAPTION_MEDIUM,
                            // eslint-disable-next-line react-native/no-inline-styles
                            {marginRight: 6, color: Colors.TEXT_PRIMARY},
                          ]}>
                          {meta.label}
                        </Text>
                      </View>
                      <Text
                        numberOfLines={1}
                        // eslint-disable-next-line react-native/no-inline-styles
                        style={[Typography.H5, {marginTop: 4}]}>
                        {env?.title || 'Untitled'}
                      </Text>
                      {!!env?.description && (
                        // eslint-disable-next-line react-native/no-inline-styles
                        <Text style={[Typography.P3, {marginTop: 4}]}>
                          {env.description}
                        </Text>
                      )}
                    </View>
                  );
                })}
              </>
            ) : null}
          </ScrollView>
        </View>
      </ForeignObject>
    </G>
  );
};
